'use client';

import { useEffect } from 'react';

import { trackEvent } from '../core';
import { createFormErrorEvent } from '../event-factories';

import type { FormError } from '../types';

type ErrorCollection = Record<string, string | undefined> | string | string[];

export type UseTrackFormErrors = (args: {
  formName: string | undefined;
  errors: ErrorCollection | undefined;
}) => void;

export const useTrackFormErrors: UseTrackFormErrors = ({
  formName,
  errors,
}) => {
  useEffect(() => {
    if (typeof errors === 'undefined' || typeof formName === 'undefined') {
      return;
    }

    const event = createEvent(formName, errors);

    if (typeof event === 'undefined') {
      return;
    }

    trackEvent(event);
  }, [formName, errors]);
};

const createEvent = (
  formName: string,
  errors: ErrorCollection | undefined,
): FormError | undefined => {
  if (typeof errors === 'undefined') {
    return undefined;
  }

  if (typeof errors === 'string') {
    return createFormErrorEvent({
      formName,
      message: errors,
    });
  }

  const messages = Array.isArray(errors)
    ? errors
    : Object.values(errors).filter(
        (message): message is string => typeof message === 'string',
      );

  if (messages.length === 0) {
    return undefined;
  }

  return createFormErrorEvent({
    formName,
    message: messages,
  });
};
